import React, { useState, useEffect } from 'react';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const TripChecklist = ({ tripPlan, onUpdate }) => {
  const [checklist, setChecklist] = useState({
    // Step 1: Plan Trip
    planTrip: {
      tripCreated: false,
      budgetSet: false,
      locationConfirmed: false,
      dateConfirmed: false,
      themeSelected: false
    },
    // Step 2: Prepare Trip
    prepareTrip: {
      accommodationBooked: false,
      restaurantBooked: false,
      transportationArranged: false,
      girlsBooked: false,
      suppliesPrepared: false
    },
    // Step 3: Activity Trip
    activityTrip: {
      tripConfirmed: false,
      girlsCheckedIn: false,
      activitiesCompleted: false,
      paymentCompleted: false,
      reviewSubmitted: false
    }
  });

  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize checklist from trip plan data
  useEffect(() => {
    if (tripPlan?.checklist) {
      setChecklist(tripPlan.checklist);
    }
  }, [tripPlan]);

  const handleCheckboxChange = async (step, item) => {
    if (!tripPlan?.id) return;

    setIsUpdating(true);
    try {
      const newValue = !checklist[step][item];
      const updatedChecklist = {
        ...checklist,
        [step]: {
          ...checklist[step],
          [item]: newValue
        }
      };

      setChecklist(updatedChecklist);

      // Update in Firestore
      const tripRef = doc(db, 'tripPlans', tripPlan.id);
      await updateDoc(tripRef, {
        checklist: updatedChecklist,
        lastUpdated: new Date()
      });

      // Notify parent component
      if (onUpdate) {
        onUpdate({ ...tripPlan, checklist: updatedChecklist });
      }
    } catch (error) {
      console.error('Error updating checklist:', error);
      // Revert on error
      setChecklist(checklist);
    } finally {
      setIsUpdating(false);
    }
  };

  const getStepProgress = (step) => {
    const items = Object.values(checklist[step]);
    const completed = items.filter(item => item).length;
    return Math.round((completed / items.length) * 100);
  };

  const getOverallProgress = () => {
    const allSteps = Object.values(checklist);
    const totalItems = allSteps.reduce((acc, step) => acc + Object.keys(step).length, 0);
    const completedItems = allSteps.reduce((acc, step) => 
      acc + Object.values(step).filter(item => item).length, 0
    );
    return Math.round((completedItems / totalItems) * 100);
  };

  const checklistItems = {
    planTrip: [
      { key: 'tripCreated', label: 'สร้าง Trip Plan', icon: '📝' },
      { key: 'budgetSet', label: 'กำหนดงบประมาณ', icon: '💰' },
      { key: 'locationConfirmed', label: 'ยืนยันสถานที่', icon: '📍' },
      { key: 'dateConfirmed', label: 'ยืนยันวันที่', icon: '📅' },
      { key: 'themeSelected', label: 'เลือก Theme', icon: '🎨' }
    ],
    prepareTrip: [
      { key: 'accommodationBooked', label: 'จองที่พัก', icon: '🏨' },
      { key: 'restaurantBooked', label: 'จองร้านอาหาร', icon: '🍽️' },
      { key: 'transportationArranged', label: 'จัดเตรียมการเดินทาง', icon: '🚗' },
      { key: 'girlsBooked', label: 'จองคิวน้อง', icon: '👩' },
      { key: 'suppliesPrepared', label: 'เตรียมอุปกรณ์', icon: '🎒' }
    ],
    activityTrip: [
      { key: 'tripConfirmed', label: 'ยืนยันทริป', icon: '✅' },
      { key: 'girlsCheckedIn', label: 'น้อง Check-in', icon: '👋' },
      { key: 'activitiesCompleted', label: 'ทำกิจกรรมเสร็จ', icon: '🎉' },
      { key: 'paymentCompleted', label: 'ชำระเงินเสร็จ', icon: '💳' },
      { key: 'reviewSubmitted', label: 'รีวิวทริป,น้อง', icon: '⭐' }
    ]
  };

  const stepTitles = {
    planTrip: 'Step 1: Plan Trip',
    prepareTrip: 'Step 2: Prepare Trip', 
    activityTrip: 'Step 3: Activity Trip'
  };

  const stepColors = {
    planTrip: 'blue',
    prepareTrip: 'green',
    activityTrip: 'purple'
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Trip Checklist</h3>
        <div className="flex items-center space-x-4">
          <div className="flex-1 bg-gray-200 rounded-full h-3">
            <div 
              className="bg-blue-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${getOverallProgress()}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium text-gray-600">
            {getOverallProgress()}% เสร็จสิ้น
          </span>
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(checklistItems).map(([stepKey, items]) => {
          const progress = getStepProgress(stepKey);
          const color = stepColors[stepKey];
          
          return (
            <div key={stepKey} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">
                  {stepTitles[stepKey]}
                </h4>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`bg-${color}-500 h-2 rounded-full transition-all duration-300`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-600">
                    {progress}%
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map((item) => (
                  <label
                    key={item.key}
                    className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                      checklist[stepKey][item.key]
                        ? `border-${color}-500 bg-${color}-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checklist[stepKey][item.key]}
                      onChange={() => handleCheckboxChange(stepKey, item.key)}
                      disabled={isUpdating}
                      className={`h-4 w-4 text-${color}-600 focus:ring-${color}-500 border-gray-300 rounded`}
                    />
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{item.icon}</span>
                      <span className={`text-sm font-medium ${
                        checklist[stepKey][item.key] ? 'text-gray-900' : 'text-gray-600'
                      }`}>
                        {item.label}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              // Auto-complete based on trip plan data
              const autoChecklist = {
                planTrip: {
                  tripCreated: true,
                  budgetSet: tripPlan?.budget > 0,
                  locationConfirmed: !!tripPlan?.tripLocation,
                  dateConfirmed: !!tripPlan?.tripDate,
                  themeSelected: !!tripPlan?.tripTheme
                },
                prepareTrip: checklist.prepareTrip,
                activityTrip: checklist.activityTrip
              };
              setChecklist(autoChecklist);
            }}
            className="p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition text-center"
          >
            <div className="text-blue-600 text-lg mb-1">🚀</div>
            <div className="text-sm font-medium text-blue-800">Auto Complete Step 1</div>
          </button>

          <button
            onClick={() => {
              // Mark all prepare trip items as completed
              const updatedChecklist = {
                ...checklist,
                prepareTrip: Object.keys(checklist.prepareTrip).reduce((acc, key) => {
                  acc[key] = true;
                  return acc;
                }, {})
              };
              setChecklist(updatedChecklist);
            }}
            className="p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition text-center"
          >
            <div className="text-green-600 text-lg mb-1">✅</div>
            <div className="text-sm font-medium text-green-800">Complete Step 2</div>
          </button>

          <button
            onClick={() => {
              // Reset all checkboxes
              const resetChecklist = {
                planTrip: Object.keys(checklist.planTrip).reduce((acc, key) => {
                  acc[key] = false;
                  return acc;
                }, {}),
                prepareTrip: Object.keys(checklist.prepareTrip).reduce((acc, key) => {
                  acc[key] = false;
                  return acc;
                }, {}),
                activityTrip: Object.keys(checklist.activityTrip).reduce((acc, key) => {
                  acc[key] = false;
                  return acc;
                }, {})
              };
              setChecklist(resetChecklist);
            }}
            className="p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition text-center"
          >
            <div className="text-gray-600 text-lg mb-1">🔄</div>
            <div className="text-sm font-medium text-gray-800">Reset All</div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TripChecklist;

